import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "mobileMenu",
    "mobileMenuButton",
    "mobileMenuClose",
    "mobileDropdown",
    "dropdownContainer",
    "dropdownMenu",
    "dropdownArrow"
  ]

  connect() {
    this.initializeState()
    this.setupEventListeners()
  }

  disconnect() {
    this.cleanupEventListeners()
  }

  initializeState() {
    // Ensure body scrolling is enabled on connect
    document.body.style.overflow = ''
    // Store timeout references for delayed hiding
    this.hideTimeouts = new Map()
  }

  setupEventListeners() {
    // Store bound functions so we can remove them later
    this.handleOutsideClick = this.handleOutsideClick.bind(this)
    this.handleEscapeKey = this.handleEscapeKey.bind(this)
    
    // Add document-level listeners
    document.addEventListener('click', this.handleOutsideClick)
    document.addEventListener('keydown', this.handleEscapeKey)
  }

  cleanupEventListeners() {
    document.removeEventListener('click', this.handleOutsideClick)
    document.removeEventListener('keydown', this.handleEscapeKey)
    
    // Clear all pending timeouts
    this.hideTimeouts.forEach(timeoutId => clearTimeout(timeoutId))
    this.hideTimeouts.clear()
    
    // Ensure body scrolling is restored
    document.body.style.overflow = ''
  }

  // Mobile menu actions
  openMobileMenu(event) {
    event.preventDefault()
    if (this.hasMobileMenuTarget) {
      this.mobileMenuTarget.classList.remove('hidden')
      this.mobileMenuTarget.scrollTop = 0
      document.body.style.overflow = 'hidden'
    }
  }

  closeMobileMenu(event) {
    if (event) {
      event.preventDefault()
    }
    if (this.hasMobileMenuTarget) {
      this.mobileMenuTarget.classList.add('hidden')
      document.body.style.overflow = ''
    }
  }

  // Mobile dropdown toggle
  toggleMobileDropdown(event) {
    event.preventDefault()
    const button = event.currentTarget
    const targetId = button.dataset.target
    const targetDropdown = document.getElementById(targetId)
    const arrowIcon = button.querySelector('svg')

    if (targetDropdown) {
      const isHidden = targetDropdown.classList.contains('hidden')
      
      if (isHidden) {
        targetDropdown.classList.remove('hidden')
        if (arrowIcon) arrowIcon.classList.add('rotate-180')
      } else {
        targetDropdown.classList.add('hidden')
        if (arrowIcon) arrowIcon.classList.remove('rotate-180')
      }
    }
  }

  // Desktop dropdown - hover to show (with delay cancellation)
  showDropdown(event) {
    const container = event.currentTarget
    const dropdownMenu = container.querySelector('.dropdown-menu')
    const arrow = container.querySelector('.dropdown-arrow')

    // Cancel any pending hide timeout for this container
    if (this.hideTimeouts.has(container)) {
      clearTimeout(this.hideTimeouts.get(container))
      this.hideTimeouts.delete(container)
    }

    if (dropdownMenu) {

      dropdownMenu.classList.remove('hidden')
      if (arrow) arrow.classList.add('rotate-180')
    }
  }

  // Desktop dropdown - hover to hide (with delay)
  hideDropdown(event) {
    const container = event.currentTarget
    const dropdownMenu = container.querySelector('.dropdown-menu')
    const arrow = container.querySelector('.dropdown-arrow')

    // Add a generous delay before hiding to allow moving to dropdown (especially for seniors)
    const timeoutId = setTimeout(() => {
      if (dropdownMenu) {
        dropdownMenu.classList.add('hidden')
        if (arrow) arrow.classList.remove('rotate-180')
      }
      this.hideTimeouts.delete(container)
    }, 300) // 300ms delay for better accessibility

    this.hideTimeouts.set(container, timeoutId)
  }

  // Keep dropdown open when hovering over the dropdown menu itself
  keepDropdownOpen(event) {
    const dropdownMenu = event.currentTarget
    const container = dropdownMenu.closest('.dropdown-container')    

    // Cancel any pending hide timeout for this container
    if (container && this.hideTimeouts.has(container)) {
      clearTimeout(this.hideTimeouts.get(container))
      this.hideTimeouts.delete(container)
    }
  }

  // Schedule dropdown hide when leaving the dropdown menu
  scheduleDropdownHide(event) {
    const dropdownMenu = event.currentTarget
    const container = dropdownMenu.closest('.dropdown-container')
    const arrow = container?.querySelector('.dropdown-arrow')

    // Add a generous delay before hiding for better accessibility
    const timeoutId = setTimeout(() => {
      dropdownMenu.classList.add('hidden')
      if (arrow) arrow.classList.remove('rotate-180')
      if (container) this.hideTimeouts.delete(container)
    }, 300) // 300ms delay for seniors

    if (container) {
      this.hideTimeouts.set(container, timeoutId)
    }
  }

  // Handle clicks outside dropdown containers
  handleOutsideClick(event) {
    // Check if click is outside all dropdown containers
    const clickedInsideDropdown = event.target.closest('.dropdown-container')
    
    if (!clickedInsideDropdown) {
      this.closeAllDesktopDropdowns()
    }
  }

  // Handle escape key
  handleEscapeKey(event) {
    if (event.key === 'Escape') {
      // Close mobile menu if open
      if (this.hasMobileMenuTarget && !this.mobileMenuTarget.classList.contains('hidden')) {
        this.closeMobileMenu()
      }
      
      // Close all desktop dropdowns
      this.closeAllDesktopDropdowns()
    }
  }

  // Helper to close all desktop dropdowns
  closeAllDesktopDropdowns() {
    // Use controller targets to scope to this instance only
    this.dropdownMenuTargets.forEach(menu => menu.classList.add('hidden'))
    this.dropdownArrowTargets.forEach(arrow => arrow.classList.remove('rotate-180'))
  }

  // Prevent dropdown from closing when clicking inside
  preventClose(event) {
    event.stopPropagation()
  }
}
